'use client';

import { useState, useEffect, useCallback } from 'react';
import { Droplet, Plus, Minus, Info, Droplets } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { waterCalculatorContent } from '../../data/water-calculator-content';

type Gender = 'male' | 'female' | 'other';
type ActivityLevel = 'low' | 'moderate' | 'high';

const WATER_PER_KG = 30; // ml per kg of body weight
const ACTIVITY_MULTIPLIER = {
  low: 1.0,
  moderate: 1.2,
  high: 1.5
};

const CONTAINER_SIZES = [100, 200, 250, 300, 350, 400, 450, 500, 750, 1000]; // in ml

export default function WaterIntakeCalculator() {
  const [weight, setWeight] = useState<number>(70);
  const [age, setAge] = useState<number>(30);
  const [gender, setGender] = useState<Gender>('male');
  const [activityLevel, setActivityLevel] = useState<ActivityLevel>('moderate');
  const [selectedContainer, setSelectedContainer] = useState<number>(250);
  const [intakeHistory, setIntakeHistory] = useState<{ amount: number; timestamp: number }[]>([]);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [customAmount, setCustomAmount] = useState<number>(0);

  // Calculate recommended daily water intake in ml
  const calculateDailyIntake = useCallback(() => {
    let baseIntake = weight * WATER_PER_KG;

    // Adjust for activity level
    baseIntake *= ACTIVITY_MULTIPLIER[activityLevel];

    // Slight adjustments based on age (older people may need more water)
    if (age > 50) {
      baseIntake *= 1.05;
    }

    // Small adjustment for gender (males typically need slightly more)
    if (gender === 'male') {
      baseIntake *= 1.05;
    }

    return Math.round(baseIntake);
  }, [weight, activityLevel, age, gender]);

  const dailyGoal = calculateDailyIntake();

  // Calculate current intake for today
  const currentIntake = intakeHistory.reduce((sum, entry) => {
    const entryDate = new Date(entry.timestamp);
    const today = new Date();

    if (
      entryDate.getDate() === today.getDate() &&
      entryDate.getMonth() === today.getMonth() &&
      entryDate.getFullYear() === today.getFullYear()
    ) {
      return sum + entry.amount;
    }
    return sum;
  }, 0);

  const progress = Math.min(100, (currentIntake / dailyGoal) * 100);

  const addWaterIntake = (amount: number) => {
    const newEntry = {
      amount,
      timestamp: Date.now(),
    };

    setIntakeHistory(prev => [...prev, newEntry]);
    setShowAddModal(false);
    setCustomAmount(0);

    // Save to localStorage
    const history = JSON.parse(localStorage.getItem('waterIntakeHistory') || '[]');
    localStorage.setItem('waterIntakeHistory', JSON.stringify([...history, newEntry]));
  };

  const removeLastEntry = () => {
    if (intakeHistory.length > 0) {
      const newHistory = [...intakeHistory];
      newHistory.pop();
      setIntakeHistory(newHistory);
      localStorage.setItem('waterIntakeHistory', JSON.stringify(newHistory));
    }
  };

  // Load history from localStorage on component mount
  useEffect(() => {
    const savedHistory = localStorage.getItem('waterIntakeHistory');
    if (savedHistory) {
      setIntakeHistory(JSON.parse(savedHistory));
    }
  }, []);

  // Get water emoji based on progress
  const getWaterEmoji = () => {
    if (progress < 30) return '💧';
    if (progress < 70) return '💦';
    if (progress < 99) return '🌊';
    return '🎉';
  };

  // Format time
  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-900/10 py-12 px-6 sm:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 px-2">
          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Droplets className="w-8 h-8 text-blue-500" />
          </div>
          <ToolPageIntro title="Water Calculator - Free Online Hydration Calculator">
                Calculate your daily water intake needs and track hydration with our free water calculator. Stay healthy and properly hydrated throughout the day.
            </ToolPageIntro>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-5 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Droplet className="w-5 h-5 mr-2 text-blue-500" />
              Your Profile
            </h2>

            <div className="space-y-4">
              <div>
                <label htmlFor="weight" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Weight (kg)
                </label>
                <input
                  type="number"
                  id="weight"
                  min="30"
                  max="200"
                  value={weight}
                  onChange={(e) => setWeight(Number(e.target.value) || 70)}
                  className="block w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700/50 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                />
              </div>

              <div>
                <label htmlFor="age" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Age
                </label>
                <input
                  type="number"
                  id="age"
                  min="1"
                  max="120"
                  value={age}
                  onChange={(e) => setAge(Number(e.target.value) || 30)}
                  className="block w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700/50 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                />
              </div>

              <div>
                <p className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Gender
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {(['male', 'female', 'other'] as Gender[]).map((g) => (
                    <button
                      key={g}
                      onClick={() => setGender(g)}
                      className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${gender === g
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                    >
                      {g.charAt(0).toUpperCase() + g.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Activity Level
                </p>
                <div className="space-y-2">
                  {([
                    { value: 'low', label: 'Low (sedentary)' },
                    { value: 'moderate', label: 'Moderate (light exercise)' },
                    { value: 'high', label: 'High (intense exercise)' },
                  ] as const).map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => setActivityLevel(value)}
                      className={`w-full text-left py-2 px-3 rounded-lg text-sm font-medium transition-colors ${activityLevel === value
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-2">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Recommended Daily Intake:
                </p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {Math.round(dailyGoal / 10) / 100} L
                  <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                    ({dailyGoal} ml)
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Progress Section */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-5 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <Droplet className="w-5 h-5 mr-2 text-blue-500" />
                  Today's Progress
                </h2>
                <div className="text-2xl">
                  {getWaterEmoji()}
                </div>
              </div>

              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300 mb-1">
                  <span>{Math.round(currentIntake / 10) / 100} L</span>
                  <span>{Math.round(dailyGoal / 10) / 100} L</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-400 to-blue-600 transition-all duration-500 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-1">
                  {Math.round(progress)}% of daily goal
                </p>
              </div>

              <div className="mb-6">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Quick Add:
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                  {[100, 200, 250, 300, 400, 500].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => addWaterIntake(amount)}
                      className="py-2 px-3 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800/40 transition-colors flex items-center justify-center"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      {amount} ml
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="w-full py-2 text-blue-600 dark:text-blue-300 border border-dashed border-blue-300 dark:border-blue-700 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                >
                  + Custom Amount
                </button>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Today's Intake
                  </h3>
                  {intakeHistory.length > 0 && (
                    <button
                      onClick={removeLastEntry}
                      className="text-xs text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 flex items-center"
                    >
                      <Minus className="w-3 h-3 mr-1" />
                      Remove Last
                    </button>
                  )}
                </div>

                {intakeHistory.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                    {intakeHistory
                      .filter(entry => {
                        const entryDate = new Date(entry.timestamp);
                        const today = new Date();
                        return (
                          entryDate.getDate() === today.getDate() &&
                          entryDate.getMonth() === today.getMonth() &&
                          entryDate.getFullYear() === today.getFullYear()
                        );
                      })
                      .map((entry, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center py-2 px-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                        >
                          <div className="flex items-center">
                            <Droplet className="w-4 h-4 text-blue-400 mr-2" />
                            <span className="text-sm text-gray-700 dark:text-gray-200">
                              {entry.amount} ml
                            </span>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {formatTime(entry.timestamp)}
                          </span>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-400 dark:text-gray-500">
                    <Droplet className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No water intake recorded today</p>
                  </div>
                )}
              </div>
            </div>

            {/* Hydration Tips */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 sm:p-5">
              <h3 className="text-sm sm:text-base font-semibold text-blue-800 dark:text-blue-200 mb-2 sm:mb-3 flex items-center">
                <Info className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" />
                Hydration Tips
              </h3>
              <ul className="text-xs sm:text-sm text-blue-700 dark:text-blue-300 space-y-1.5">
                <li className="flex items-start">
                  <span className="mr-1.5">•</span>
                  <span>Drink a glass of water first thing in the morning</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-1.5">•</span>
                  <span>Carry a reusable water bottle with you</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-1.5">•</span>
                  <span>Drink before you feel thirsty - thirst is a late sign of dehydration</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-1.5">•</span>
                  <span>Eat water-rich foods like fruits and vegetables</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Add Custom Amount Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Add Custom Amount
            </h3>

            <div className="mb-6">
              <label htmlFor="customAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Amount (ml)
              </label>
              <input
                type="number"
                id="customAmount"
                min="1"
                value={customAmount || ''}
                onChange={(e) => setCustomAmount(Number(e.target.value) || 0)}
                className="block w-full px-4 py-3 text-lg border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700/50 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                placeholder="e.g. 350"
                autoFocus
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => customAmount > 0 && addWaterIntake(customAmount)}
                disabled={!customAmount}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${customAmount
                  ? 'bg-blue-500 hover:bg-blue-600'
                  : 'bg-blue-300 dark:bg-blue-700 cursor-not-allowed'
                  }`}
              >
                Add {customAmount || 0} ml
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Informational Content */}
      <div className="mt-12">
        <InformationalContent
          title={waterCalculatorContent.title}
          description={waterCalculatorContent.description}
          content={waterCalculatorContent.content}
          faqs={waterCalculatorContent.faqs}
          relatedTools={waterCalculatorContent.relatedTools}
        />
      </div>
    </div>
  );
}
